import { defineStore } from "pinia";
import moment from "moment";
import { ElMessageBox, ElNotification } from "element-plus";
import {
  bulkDeleteRequest,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
  download,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { useConfigStore } from "~/stores/configStore";
import { useTopBarBadgeStore } from "~/stores/topBarBadgeStore";
import { Ticket } from "~/projects/help-desk/types";
import { CallHistory } from "~/types";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { Comment, OtherLeadCommentGroup } from "~/types/comment";
import { Task } from "~/projects/task-manager/types/task";
import { LeadsFollowupTasks } from "~/.nuxt/components";
import { LeadsByStatus } from "~/projects/crm/types/leadsByStatus";
import { LeadFollowUp } from "~/projects/crm/types/leadFollowUp";
import { LeadView } from "~/projects/crm/types/enums/leadView";
import { LeadAbleType } from "~/projects/crm/types/enums/leadAbleType";
import { Lead, LeadProspect, LeadStatus } from "~/projects/crm/types";
import { Referral } from "~/types/referral";

interface LeadAttachment {
  file: File;
  name: string;
  description?: string;
}

// Recursively serialize a value into FormData using Laravel's bracket notation
// (e.g. assigned_users[0][id], profile[name]) so nested lead data survives a
// multipart request.
const appendToFormData = (formData: FormData, key: string, value: any) => {
  if (value === null || value === undefined) return;

  if (value instanceof File || value instanceof Blob) {
    formData.append(key, value);
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendToFormData(formData, `${key}[${index}]`, item);
    });
  } else if (typeof value === "object") {
    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
      appendToFormData(formData, `${key}[${nestedKey}]`, nestedValue);
    });
  } else if (typeof value === "boolean") {
    formData.append(key, value ? "1" : "0");
  } else {
    formData.append(key, value as string);
  }
};

const buildLeadFormData = (lead: Lead, attachments: LeadAttachment[] = []) => {
  const formData = new FormData();

  Object.entries(lead).forEach(([key, value]) => {
    appendToFormData(formData, key, value);
  });

  attachments.forEach((attachment, index) => {
    if (attachment?.file) {
      formData.append(`attachments[${index}][file]`, attachment.file);
    }
    formData.append(`attachments[${index}][name]`, attachment.name ?? "");
    formData.append(
      `attachments[${index}][description]`,
      attachment.description ?? "",
    );
  });

  return formData;
};

export const useLeadsStore = defineStore("leadsStore", {
  state: () => {
    const configStore = useConfigStore();
    return {
      _view: LeadView.BOARD,
      _tableData: [] as Array<Lead>,
      _selectedLeadForEdit: {} as Lead,
      _selectedLeadForDuplicate: {} as Lead,
      multipleSelection: [] as Array<Lead>,
      _filterData: {
        show_type: "not_converted",
      } as Record<string, any>,
      leadsFollowupTasksLoading: false,
      _submittedFilterData: {},
      _statusLeads: [] as LeadsByStatus[],
      _pagination: {} as Pagination,
      _leadTotalCount: 0,
      _leadTotalAmount: 0,
      _lead: {} as Lead,
      _leadTasks: {} as any,
      _parsedLeadInfo: {} as any,
      _lastParseParams: null as {
        profile_id: number;
        leadable_id: number;
        leadable_type: string;
        text: string;
      } | null,
      _leadFormSubmissionColumn: null as any,
      _templateLeadParsingColumn: null as any,
      _publicUrl: "",
      _leadFormSubmissionToken: null as string | null,
      tableLoader: true,
      boardLoader: true,
      showLeadEditModal: false,
      showBulkUpload: false,
      showReassign: false,
      _showFormSubmissionUrlModal: false,
      _leadFollowupTasks: [] as Task[],
      // Today's still-open follow-ups of the logged-in agent. This is the modal
      // table's row data only, and it is lazily loaded — it stays empty until
      // the agent opens the modal. The top-bar badge is counted separately from
      // the id list in topBarBadgeStore, so it is correct even while this is
      // empty.
      _todayFollowUps: [] as any[],
      // Whether _todayFollowUps currently holds a complete server snapshot.
      // The invariant is that the array is either exactly what the endpoint
      // returned (possibly then patched by socket events) or empty — never a
      // partial set, because the modal presents it as the whole of today.
      _todayFollowUpsLoaded: false,
      todayFollowUpsLoading: false,
      showTodayFollowUpsModal: false,
      showLeadsFiltersEditModal: false,
      // for leads table view
      showTableLeadEditModal: false,
      showLeadCreateModal: false,
      showCreateDialog: false,
      // for leads list view
      showListView: false,
      showListEditDialog: false,
      showListCreateDialog: false,
      showListDuplicateDialog: false,
      createLeadStatus: configStore.globalConfig?.lead_statuses?.[0]
        ?.name as LeadStatus,
      _leadableData: null as
        | null
        | (Pick<Lead, "leadable_id" | "leadable_type"> & {
            channel_id?: number;
            channel_type?: "meta_page" | "whatsapp_account";
            // Resolved by the caller (the inbox knows whether the referral sits
            // on the ticket or on one of its info conversations); `referral` is
            // only carried so the create form can name it.
            referral_id?: number | null;
            referral?: Referral | null;
            ctwa_clid?: string | null;
          }),

      // for leads Comment
      _leadComments: [] as Comment[],
      // Comments on the profile's OTHER leads, grouped by lead.
      _otherLeadComments: [] as OtherLeadCommentGroup[],
      _otherLeadCommentsLoading: false,

      selectedLeads: [] as Array<Lead>,
      _leadAllAttachments: {} as {
        lead_attachments: any[];
        profile_attachments: any[];
        task_attachments: any[];
      },
    };
  },
  getters: {
    showEditOptions(state) {
      return state.multipleSelection.length > 0;
    },
    tableData: (state) => state._tableData,
    statusLeads: (state) => state._statusLeads,
    selectedLeadForEdit: (state) => state._selectedLeadForEdit,
    selectedLeadForDuplicate: (state) => state._selectedLeadForDuplicate,
    pagination: (state) => state._pagination,
    leadTotalCount: (state) => state._leadTotalCount,
    leadTotalAmount: (state) => state._leadTotalAmount,
    lead: (state) => state._lead,
    leadTasks: (state) => state._leadTasks,
    view: (state) => state._view,
    comments: (state) => state._leadComments,
    otherLeadComments: (state) => state._otherLeadComments,
    leadFollowupTasks: (state) => state._leadFollowupTasks,
    todayFollowUps: (state) => state._todayFollowUps,
    // The modal's own header count — derived, so it can never drift from the
    // table it sits above. The top-bar badge is a different number entirely
    // (topBarBadgeStore.todayFollowUpCount), because this list is only
    // populated once the modal has been opened.
    todayFollowUpsCount: (state) => state._todayFollowUps.length,
    leadAllAttachments: (state) => state._leadAllAttachments,
    leadFormSubmissionColumn: (state) => state._leadFormSubmissionColumn,
    templateLeadParsingColumn: (state) => state._templateLeadParsingColumn,
    parsedLeadInfo: (state) => state._parsedLeadInfo,
    // The server takes the profile off the ticket's customer and overwrites
    // whatever was sent, so the create form must not offer a choice.
    isFromTicket: (state) =>
      state._leadableData?.leadable_type === LeadAbleType.ticket,
  },
  actions: {
    async getLeads(pageNo = 1, sortBy = "id", ascending = false) {
      this.tableLoader = true;
      try {
        const sortDir = ascending ? "asc" : "desc";
        let url = `crm/leads?pagination=true&page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
        for (const [key, value] of Object.entries(this._filterData)) {
          if (value === null) continue;
          url += `&${key}=${encodeURIComponent(value)}`;
        }

        const { response, error } = await getRequest<
          TableDataWithPagination<Lead>
        >(url);
        if (!error) {
          const { data, ...pagination } = response.data;
          this.$state._tableData = data;
          this.$state._pagination = pagination;
        }
      } finally {
        this.tableLoader = false;
      }
    },
    async exportTable() {
      let url = `crm/leads/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${encodeURIComponent(value)}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"leads-report"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },
    async getStatusLeads() {
      this.boardLoader = true;
      // change api
      try {
        let url = `crm/leads/with-status?`;
        for (const [key, value] of Object.entries(this._filterData)) {
          if (value === null) continue;
          url += `&${key}=${encodeURIComponent(value)}`;
        }
        const { response, error } = await getRequest<LeadsByStatus[]>(url);

        this.$state._leadTotalCount = response.data.reduce(
          (total, item) => total + (item.count || 0),
          0,
        );

        this.$state._leadTotalAmount = response.data.reduce(
          (sum, lead) => sum + (lead.amount || 0),
          0,
        );

        if (!error) {
          const { data } = response;
          this.$state._statusLeads = data;
        }
      } finally {
        this.boardLoader = false;
      }
    },
    // Appends the next slice of one board column.
    //
    // The column and the paging cursor are arguments rather than keys parked on
    // _filterData, because `status` there is the agent's own "Stage" filter
    // (Filters.vue). Writing the scrolled column's id over it re-scoped every
    // later request to that one column, which is why each caller then had to
    // delete both keys again — and why the callers that only deleted `offset`
    // quietly refetched a one-column board.
    async getStatusLeadsByOffset(
      statusId: number,
      offset: number,
      length = 10,
    ) {
      let url = `crm/leads/by-offset?status=${encodeURIComponent(
        statusId,
      )}&offset=${offset}&length=${length}`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        // The column being paged is narrower than any status the agent could
        // have filtered to, so it wins; the rest of the filters still apply.
        if (key === "status" || key === "offset" || key === "length") continue;
        url += `&${key}=${encodeURIComponent(value)}`;
      }
      const { response, error } = await getRequest<LeadsByStatus[]>(url);

      if (!error) {
        const { data } = response;

        if (!data || data.length === 0) {
          return;
        }

        // Only a column already on the board can be scrolled, so a miss means
        // the board was replaced mid-request — appending would build a group
        // with no name or count behind it.
        const statusGroup = this.$state._statusLeads.find(
          (group) => group.id === statusId,
        );
        if (!statusGroup) return;

        // Only append if the column is still exactly where this request started.
        // Nothing cancels an in-flight page, so a response can land after the
        // board has been refetched and rebuilt — appending it then would splice
        // a later slice onto a fresh first page and leave a hole in the list.
        if (statusGroup.leads.length !== offset) return;

        statusGroup.leads.push(...data);
      }
    },

    async bulkAssignLeads(data: {
      lead_ids: number[];
      assigned_users: { id: number; type?: string | null }[];
    }) {
      const { response, error } = await postRequest<any>(
        "crm/leads/bulk-assign",
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return { response, error };
    },

    async moveLead(leadData: Partial<Lead>) {
      const { response, error } = await postRequest<Lead>(
        // change api
        `crm/leads/${leadData.id}/status`,
        leadData,
      );
      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (Array.isArray(data.errors)) {
            errorMessage = data.errors.join("<br/>");
          } else if (data.errors && typeof data.errors === "object") {
            const formattedErrors = [];
            for (const [key, value] of Object.entries(data.errors)) {
              const messages = Array.isArray(value) ? value : [value];
              formattedErrors.push(...messages);
            }
            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox({
          title: "Operation failed",
          message: errorMessage,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      }
      if (!error) {
        return response.data;
      }
      return error;
    },
    // Status-only update for screens that never loaded the whole lead — the
    // inbox's Leads History list, for one. It posts to the same endpoint as
    // `moveLead`, but answers with a plain success flag: `move` replies 202
    // with an empty body, so `moveLead`'s return value can't tell a rejected
    // change apart from an applied one.
    async changeLeadStatus(leadId: number, statusId: number): Promise<boolean> {
      const { error } = await postRequest(`crm/leads/${leadId}/status`, {
        status_id: statusId,
      });

      if (!error) return true;

      const data = error.response?.data as any;
      const errors = data?.errors;

      let errorMessage = "";
      if (Array.isArray(errors)) {
        errorMessage = errors.join("<br/>");
      } else if (errors && typeof errors === "object") {
        errorMessage = Object.values(errors).flat().join("<br/>");
      } else {
        errorMessage =
          data?.message || error.message || "Unknown error occurred.";
      }

      ElMessageBox({
        title: "Operation failed",
        message: errorMessage,
        type: "error",
        dangerouslyUseHTMLString: true,
      });

      return false;
    },
    async createLead(lead: Lead, attachments: LeadAttachment[] = []) {
      if (this._leadableData) {
        lead.leadable_id = this._leadableData.leadable_id;
        lead.leadable_type = this._leadableData.leadable_type;
        if (this._leadableData.channel_id) {
          lead.channel_id = this._leadableData.channel_id;
        }
        if (this._leadableData.channel_type) {
          lead.channel_type = this._leadableData.channel_type;
        }
        if (this._leadableData.referral_id) {
          lead.referral_id = this._leadableData.referral_id;
        }
        if (this._leadableData.ctwa_clid) {
          lead.ctwa_clid = this._leadableData.ctwa_clid;
        }
      }
      const hasAttachments = attachments.length > 0;
      const { response, error } = await postRequest<Lead>(
        "crm/leads",
        hasAttachments ? buildLeadFormData(lead, attachments) : lead,
        true,
        hasAttachments
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : ({} as any),
      );
      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (data.errors && typeof data.errors === "object") {
            const formattedErrors: string[] = [];

            for (const [row, messages] of Object.entries(data.errors)) {
              const msgArray = Array.isArray(messages) ? messages : [messages];
              msgArray.forEach((msg) => {
                formattedErrors.push(`${row}: ${msg}`);
              });
            }

            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox.alert(errorMessage, "Error", {
          dangerouslyUseHTMLString: true,
          type: "error",
        });

        return;
      } else if (!error) {
        // The create form can attach follow-ups to the new lead, and those are
        // created server-side — recount the badge when it did.
        if ((lead as any)?.follow_ups?.length) {
          await this.refreshTodayFollowUpCount();
        }
        return response;
      }
      return null;
    },
    async updateLead(lead: Lead, attachments: LeadAttachment[] = []) {
      const hasAttachments = attachments.length > 0;

      // Laravel can't parse multipart bodies on a real PUT, so when files are
      // present we POST with method spoofing; otherwise keep the JSON PUT.
      let response;
      let error;
      if (hasAttachments) {
        const formData = buildLeadFormData(lead, attachments);
        formData.append("_method", "put");
        ({ response, error } = await postRequest<Lead>(
          `crm/leads/${lead.id}`,
          formData,
          true,
          { headers: { "Content-Type": "multipart/form-data" } },
        ));
      } else {
        ({ response, error } = await putRequest<Lead>(
          `crm/leads/${lead.id}`,
          lead,
        ));
      }
      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (Array.isArray(data.errors)) {
            errorMessage = data.errors.join("<br/>");
          } else if (data.errors && typeof data.errors === "object") {
            const formattedErrors = [];
            for (const [key, value] of Object.entries(data.errors)) {
              const messages = Array.isArray(value) ? value : [value];
              formattedErrors.push(...messages);
            }
            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox({
          title: "Operation failed",
          message: errorMessage,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      }
      if (!error) {
        ElNotification({
          type: "success",
          title: "Updated",
          message: "Lead stage updated",
        });
        return response.data;
      }
      // return null;
    },
    async deleteLead(lead: Lead) {
      return await deleteRequest(`crm/leads/${lead.id}`);
    },
    // Bulk counterpart of deleteLead. The endpoint deletes the ids one at a
    // time, so a request that fails part way still removed the leads it got
    // through — callers refetch rather than splicing _tableData themselves.
    async bulkLeadDelete(leadIds: number[]) {
      const result = await bulkDeleteRequest("crm/leads/bulk", {
        lead_ids: leadIds,
      });

      // bulkDeleteRequest resolves with the response on success and with the
      // AxiosError on failure. Sorting the two out here keeps call sites on the
      // { response, error } shape the rest of this store hands back.
      return result instanceof Error
        ? { response: null, error: result }
        : { response: result, error: null };
    },
    async getLeadsCollection() {
      let url = `crm/leads?`;
      for (const key in this._filterData) {
        // @ts-ignore
        url += `&${key}=${encodeURIComponent(this._filterData[key])}`;
      }

      const { response, error } = await getRequest<Lead[]>(url);
      if (!error) {
        return response.data;
      }
      return null;
    },
    // lead import columns
    // async getLeadsImportColumns() {
    //   const url = `crm/leads/lead-import-columns`;

    //   const { response, error } = await getRequest<Lead[]>(url);
    //   if (!error) {
    //     return response.data;
    //   }
    //   return null;
    // },
    async getLeadsImportColumns() {
      const url = `crm/leads/lead-import-settings`;

      const { response, error } = await getRequest<Lead[]>(url);
      if (!error) {
        return response.data;
      }
      return null;
    },
    async updateLeadImportColumns(data: any) {
      const { response, error } = await putRequest<any>(
        "crm/leads/lead-import-settings",
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return { response, error };
    },
    // template lead parsing columns

    async getTemplateLeadParsingColumns() {
      const url = `crm/template-lead-parser/settings`;

      const { response, error } = await getRequest<Lead[]>(url);

      if (error) return { error };

      this._templateLeadParsingColumn = response.data;
      return { data: response.data };
    },
    async updateTemplateLeadParsingColumns(data: any) {
      const { response, error } = await putRequest<any>(
        "crm/template-lead-parser/settings",
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return { response, error };
    },

    async parseLead(data: {
      profile_id: number;
      leadable_id: number;
      leadable_type: string;
      text: string;
    }) {
      const { response, error } = await postRequest<any>(
        "crm/template-lead-parser/parse",
        data,
      );
      if (error) {
        ElMessageBox({
          title: "Error Parsing Lead",
          message:
            error?.response?.data?.message ||
            "An error occurred while parsing Lead.",
          type: "error",
        });
      } else if (!error) {
        this._parsedLeadInfo = response.data;
        this._lastParseParams = data;
      }
      return { response, error };
    },

    async reparseLead() {
      if (!this._lastParseParams) return;
      return await this.parseLead(this._lastParseParams);
    },

    async updateParsedLead(data: {
      lead_id?: number | null;
      profile_id?: number | null;
      data: { key: string; value: string }[];
    }) {
      const { response, error } = await putRequest<any>(
        `crm/template-lead-parser/apply`,
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (Array.isArray(data.errors)) {
            errorMessage = data.errors.join("<br/>");
          } else if (data.errors && typeof data.errors === "object") {
            const formattedErrors = [];
            for (const [key, value] of Object.entries(data.errors)) {
              const messages = Array.isArray(value) ? value : [value];
              formattedErrors.push(...messages);
            }
            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox({
          title: "Operation failed",
          message: errorMessage,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      }

      return { response, error };
    },

    async getLeadsFormSubmissionColumns() {
      const url = `crm/lead-form-submissions/settings`;

      const { response, error } = await getRequest<Lead[]>(url);

      if (error) return { error };

      this._leadFormSubmissionColumn = response.data;
      return { data: response.data };
    },

    async updateLeadFormSubmissionColumns(data: any) {
      const { response, error } = await putRequest<any>(
        "crm/lead-form-submissions/settings",
        data,
        true,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return { response, error };
    },

    async getLeadsSubmissionForm(token?: string) {
      let url = `crm/lead-form-submissions/form-data`;
      const queryString = window.location.search;

      if (token) {
        this.$state._leadFormSubmissionToken = token;
        url = `${url}?token=${token}`;
      }

      const { response, error } = await getRequest<Lead[]>(url);

      if (error) {
        ElMessageBox({
          title: "Error Fetching Lead Form Data",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });
      }
      if (!error) {
        return response.data;
      }

      return null;
    },

    async updateLeadsSubmissionForm(data: any) {
      let endpoint = "crm/lead-form-submissions/submit";

      if (this.$state._leadFormSubmissionToken) {
        endpoint = `${endpoint}?token=${this.$state._leadFormSubmissionToken}`;
      }

      const { response, error } = await putRequest<any>(endpoint, data, false, {
        headers: { "Content-Type": "application/json" },
      });
      if (error) {
        const status = error.response?.status;
        let message = "Something went wrong";
        let formattedErrors = "";

        if (status == 422) {
          const errors = error.response?.data?.errors || {};

          formattedErrors = `<ul style="list-style-type: disc; padding-left: 20px;">`;

          Object.keys(errors).forEach((key) => {
            errors[key].forEach((message) => {
              formattedErrors += `<li>${message}</li>`;
            });
          });

          formattedErrors += "</ul>";
        } else {
          message = error.response?.data?.message || error.message || message;
        }

        ElMessageBox({
          title: "Public Lead Submission Failed",
          message: formattedErrors || message,
          dangerouslyUseHTMLString: true,
          type: "info",
        });

        return;
      }

      return { response, error };
    },

    async getLeadsFormSubmissionUrl(
      submissionId?: number,
      leadId?: number,
      preview = false,
    ) {
      // The form is only reachable through a token link: one made for a
      // customer, or the admin's own short-lived preview of it.
      let url = `crm/lead-form-submissions/url?preview=${preview ? 1 : 0}`;

      if (submissionId) {
        url += `&submission=${submissionId}`;
      } else if (leadId) {
        url += `&lead=${leadId}`;
      }

      const { response, error } = await getRequest<any>(url, true);

      if (error) {
        ElMessageBox({
          title: "Error Fetching URL",
          message:
            error?.response?.data?.message || error?.response?.data?.error,
          type: "error",
        });

        return { response, error };
      }

      if (!error) {
        this.$state._publicUrl = response.data.url;

        return { response, error };
      }
    },

    async leadsBulkInsert(data: any) {
      const { response, error } = await postRequest<any>(
        "crm/leads/bulk-upload",
        data,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return { response, error };
    },

    async downloadSampleExcelFile() {
      await download(
        "/download-sample-file/sample-leads-import.xlsx",
        "sample-leads-import.xlsx",
      );
    },
    async getLead(leadId: number) {
      const { response, error } = await getRequest<Lead>(`crm/leads/${leadId}`);
      if (!error) {
        this.$state._lead = response.data;
        this.$state._selectedLeadForEdit = response.data;
      }
    },
    async getLeadAllAttachments(leadId: number) {
      const { response, error } = await getRequest<Lead>(
        `crm/leads/${leadId}/attachments`,
      );
      if (!error) {
        this.$state._leadAllAttachments = response.data;
      }
    },

    async getLeadTasks(leadId: number) {
      const { response, error } = await getRequest(`crm/leads/${leadId}/tasks`);
      if (!error) {
        this.$state._leadTasks = response.data;
      }
    },

    async getLeadFollowupTasks(leadId: number): Promise<Task[]> {
      this.leadsFollowupTasksLoading = true;
      const { response, error } = await getRequest<Task[]>(
        `crm/lead-follow-ups?lead_id=${leadId}`,
      );
      this.leadsFollowupTasksLoading = false;
      if (!error) {
        this.$state._leadFollowupTasks = response.data;
        return response.data;
      } else {
        ElMessageBox({
          title: "Follow up task creation failed",
          message:
            error?.response?.data?.message ||
            "An error occurred while getting the follow-up tasks.",
          type: "error",
        });

        // Throwing an error here ensures the return type is satisfied
        throw new Error(
          error?.response?.data?.message || "Follow up task retrieval failed",
        );
      }
    },

    // Feeds the modal table. The top-bar badge no longer waits on this call —
    // its number arrives with the rest of the badges from /topbar/items/counts
    // — so this only runs when the agent opens the modal, or when a follow-up
    // changes while the modal is open. `silent` is for those secondary
    // refreshes, where a failure must not throw a dialog over the app.
    async getTodayFollowUps({ silent = false } = {}): Promise<any[]> {
      this.todayFollowUpsLoading = true;
      // This is the other full-snapshot writer of the badge's id set, so it
      // takes a seed generation before its request goes out for the same reason
      // getTopBarBadgeCount does — see beginTodayFollowUpSeed.
      const badgeStore = useTopBarBadgeStore();
      const generation = badgeStore.beginTodayFollowUpSeed();
      try {
        const { response, error } = await getRequest<any[]>(
          "crm/lead-follow-ups/today",
        );
        if (error) {
          if (!silent) {
            ElMessageBox({
              title: "Today's follow ups could not be loaded",
              message:
                error?.response?.data?.message ||
                "An error occurred while getting today's follow-ups.",
              type: "error",
            });
          }
          // A failed fetch leaves nothing trustworthy behind. Anything already
          // in the array came from socket events on a list that was never
          // loaded, and keeping it would let the modal present one or two rows
          // as the complete day for the rest of the session.
          this.$state._todayFollowUps = [];
          this.$state._todayFollowUpsLoaded = false;
          return this.$state._todayFollowUps;
        }

        // Whatever the modal is holding right now, kept before anything is
        // assigned: rows the socket delivered while this request was in flight
        // live only here, so they are the fallback the reconciliation below
        // looks into.
        const previousFollowUps = this.$state._todayFollowUps;
        const snapshot: any[] = response.data ?? [];

        // This list is the same set the badge counts, so a fetch also
        // reconciles the ids the top bar seeded at page load. Seeding ids
        // rather than a number is what makes the socket path safe: a row that
        // arrives twice — once here, once over the wire — is still one id.
        const committedIds: number[] | null = badgeStore.setTodayFollowUpIds(
          snapshot.map((f) => f.id),
          generation,
        );

        // A newer seed superseded this flight, so the snapshot is stale and the
        // badge refused it. Assigning its rows anyway would put the modal on a
        // snapshot the badge has already rejected, and marking the list loaded
        // off it would let socket events start patching that stale set. Leave
        // everything as it is: the newer seed commits shortly and reconciles
        // both views together.
        if (committedIds === null) {
          return this.$state._todayFollowUps;
        }

        // The rows are rebuilt FROM the committed ids rather than taken from
        // the response, because the two can legitimately disagree: the server
        // computes its snapshot at request time, and socket events that landed
        // between then and now were already applied to the badge's id set (and
        // replayed on top of this snapshot by setTodayFollowUpIds). So the
        // committed ids are the authority for what is in today's list, and the
        // rows are only looked up to match them, in a table where the snapshot's
        // row wins and a row the modal already held covers any committed id the
        // snapshot pre-dates — an addition whose row only ever arrived over the
        // socket. Overwriting with the raw response instead resurrected rows for
        // follow-ups that had just been completed, leaving the modal a row
        // ahead of the badge with a phantom row that errors on Complete.
        const rowsById = new Map<number, any>();
        previousFollowUps.forEach((followUp) => {
          rowsById.set(Number(followUp.id), followUp);
        });
        snapshot.forEach((followUp) => {
          rowsById.set(Number(followUp.id), followUp);
        });
        this.$state._todayFollowUps = committedIds
          .map((id) => rowsById.get(Number(id)))
          // An id with no row on either side cannot be rendered — it is still
          // counted by the badge, and the next fetch picks its row up.
          .filter((followUp) => Boolean(followUp))
          // Match the order the endpoint hands rows back in — the same ordering
          // upsertTodayFollowUp maintains.
          .sort((a, b) =>
            String(a.follow_up_at ?? "").localeCompare(
              String(b.follow_up_at ?? ""),
            ),
          );
        this.$state._todayFollowUpsLoaded = true;
        return this.$state._todayFollowUps;
      } finally {
        this.todayFollowUpsLoading = false;
      }
    },

    // Merges a follow-up pushed over the socket into the modal's row list. An
    // id already in the list is replaced in place rather than appended, so an
    // edit or a re-broadcast refreshes the row instead of duplicating it, and
    // the list is re-sorted afterwards because the endpoint hands it back
    // ordered by follow_up_at — the modal must not visibly reorder itself the
    // moment a row lands.
    //
    // This only maintains the row array, which is lazily loaded and empty until
    // the agent opens the modal. It deliberately does NOT touch the top-bar
    // badge: that number comes solely from the id list in topBarBadgeStore, and
    // having two writers for it is exactly the drift this design removes.
    //
    // And it does nothing at all until that list has actually been loaded.
    // Patching an unloaded array would turn it from "nothing yet" into "one
    // row", which the modal then shows as the complete day next to a badge
    // reading five. The badge's id set already covers the not-yet-opened case —
    // that independence is the whole point of keeping the two numbers apart.
    upsertTodayFollowUp(followUp: any) {
      if (!this.$state._todayFollowUpsLoaded || !followUp?.id) {
        return;
      }
      const index = this.$state._todayFollowUps.findIndex(
        (f) => f.id === followUp.id,
      );
      if (index === -1) {
        this.$state._todayFollowUps.push(followUp);
      } else {
        this.$state._todayFollowUps.splice(index, 1, followUp);
      }
      this.$state._todayFollowUps.sort((a, b) =>
        String(a.follow_up_at ?? "").localeCompare(
          String(b.follow_up_at ?? ""),
        ),
      );
    },

    // Drops a follow-up that stopped qualifying for today's list. Same rules as
    // upsertTodayFollowUp: rows only, the badge is the id list's business, and
    // nothing to do while the list has never been loaded.
    removeTodayFollowUp(id: number) {
      if (!this.$state._todayFollowUpsLoaded) {
        return;
      }
      this.$state._todayFollowUps = this.$state._todayFollowUps.filter(
        (f) => f.id !== id,
      );
    },

    // Keeps the top-bar badge honest after a follow-up is created, edited or
    // completed. With the modal open its list covers exactly what the badge
    // counts, so refetching it updates both; otherwise only the number has to
    // come back, and it rides along with the other badge counts.
    //
    // The socket now delivers the same change too, and both paths are kept on
    // purpose: this one is the fallback for a dropped or disconnected socket.
    // Running both is harmless because the badge is a set of ids — re-adding an
    // id the socket already added is a no-op, so nothing double-counts.
    async refreshTodayFollowUpCount() {
      if (this.showTodayFollowUpsModal) {
        await this.getTodayFollowUps({ silent: true });
        return;
      }
      await useTopBarBadgeStore().getTopBarBadgeCount();
    },

    setTodayFollowUpsModalVisibility(value: boolean) {
      this.showTodayFollowUpsModal = value;
    },

    async createLeadFollowUp(
      taskData: Partial<LeadFollowUp>,
    ): Promise<LeadFollowUp> {
      const { response, error } = await postRequest<LeadFollowUp>(
        "crm/lead-follow-ups",
        taskData,
      );

      if (!error) {
        // The new follow-up may be due today, so the top-bar badge has to be
        // recounted right away rather than waiting for the next page load.
        await this.refreshTodayFollowUpCount();
        return response.data;
      } else {
        ElMessageBox({
          title: "Follow up task creation failed",
          message:
            error?.response?.data?.message ||
            "An error occurred while creating the follow-up task.",
          type: "error",
        });

        // Throwing an error here ensures the return type is satisfied
        throw new Error(
          error?.response?.data?.message || "Follow up task creation failed",
        );
      }
    },

    async updateLeadFollowUp(
      taskData: Partial<LeadFollowUp>,
    ): Promise<LeadFollowUp | undefined> {
      const { response, error } = await putRequest<LeadFollowUp>(
        `crm/lead-follow-ups/${taskData.id}`,
        taskData,
      );

      if (!error) {
        // An edit can move a follow-up into or out of today, either way the
        // badge has to be recounted.
        await this.refreshTodayFollowUpCount();
        return response.data;
      }

      ElMessageBox({
        title: "Follow up update failed",
        message:
          error?.response?.data?.message ||
          "An error occurred while updating the follow-up task.",
        type: "error",
      });

      return undefined;
    },

    // Capture the active lead filters to scope a bulk action.
    // Mirrors getLeads() query serialization: arrays are coerced to a comma-joined
    // string (the backend LeadFilterTrait does explode(',') on them), and empty
    // values/arrays are dropped so e.g. an untouched multi-select isn't sent.
    // Everything in _filterData is now a filter the agent actually chose — the
    // board's paging cursor lives with the board, not here.
    leadFilterSnapshot(): Record<string, any> {
      const filters: Record<string, any> = {};
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null || value === undefined || value === "") continue;
        if (Array.isArray(value)) {
          if (value.length === 0) continue;
          filters[key] = value.join(",");
          continue;
        }
        filters[key] = value;
      }
      return filters;
    },

    async previewLeadBulkSms(phoneOption: "default" | "all") {
      const { response, error } = await postRequest<any>(
        "crm/leads/send-sms/preview",
        {
          phone_option: phoneOption,
          filters: this.leadFilterSnapshot(),
        },
      );

      if (!error) return response.data;

      ElMessageBox.alert(
        error.response?.data?.message || "Failed to fetch recipient count",
        "Error",
        { confirmButtonText: "OK", type: "error" },
      );
      return null;
    },

    async sendLeadBulkSms(data: {
      text: string;
      phone_option: "default" | "all";
      scheduled_at?: string | null;
    }) {
      const { response, error } = await postRequest<any>("crm/leads/send-sms", {
        ...data,
        filters: this.leadFilterSnapshot(),
      });

      if (!error) return response.data;

      ElMessageBox.alert(
        error.response?.data?.message || "Failed to send bulk SMS",
        "Error",
        { confirmButtonText: "OK", type: "error" },
      );
      return null;
    },

    async closeFollowUpTask(leadFollowUp: Partial<LeadFollowUp>) {
      const { response, error } = await putRequest<Task>(
        `crm/lead-follow-ups/${leadFollowUp.id}/close`,
      );
      if (!error) {
        ElNotification({
          type: "success",
          title: "Updated",
          message: "Follow up completed",
        });
        // Completing removes it from today's open set — drop it from the badge
        // no matter which screen completed it.
        await this.refreshTodayFollowUpCount();
        return response.data;
      }
      if (error) {
        let errorMessage = "";

        if (error.response) {
          const data = error.response.data;

          if (Array.isArray(data.errors)) {
            errorMessage = data.errors.join("<br/>");
          } else if (data.errors && typeof data.errors === "object") {
            const formattedErrors = [];
            for (const [key, value] of Object.entries(data.errors)) {
              const messages = Array.isArray(value) ? value : [value];
              formattedErrors.push(...messages);
            }
            errorMessage = formattedErrors.join("<br/>");
          } else if (data.message) {
            errorMessage = data.message;
          } else {
            errorMessage = "Unknown error occurred.";
          }
        } else {
          errorMessage = error.message || "Unknown error occurred.";
        }

        ElMessageBox({
          title: "Operation failed",
          message: errorMessage,
          type: "error",
          dangerouslyUseHTMLString: true,
        });

        return;
      }
      return error.response?.data;
    },

    async getLeadAble(id: number) {
      return await getRequest<Ticket | CallHistory>(`crm/leads/${id}/leadable`);
    },

    // attachments

    async addLeadAttachment(data: any) {
      const { response, error } = await postRequest<any>(
        "crm/attachments",
        data,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (!error) {
        return response;
      }
      return null;
    },

    async deleteLeadAttachment(id: number) {
      return await deleteRequest(`crm/attachments/${id}`);
    },

    /**
     * Comments left on the profile's OTHER leads, grouped by lead.
     *
     * Kept out of the lead page's initial chain and fired last: its cost scales
     * with how much history the customer has rather than with this lead, so it
     * can be slow on long-lived profiles. The loading flag lets the section
     * show a spinner instead of quietly appearing late.
     */
    async getOtherLeadComments(id: number) {
      this._otherLeadCommentsLoading = true;

      try {
        const { response, error } = await getRequest<OtherLeadCommentGroup[]>(
          `crm/leads/${id}/other-lead-comments`,
        );

        this._otherLeadComments = error ? [] : response.data;
      } finally {
        this._otherLeadCommentsLoading = false;
      }
    },
    async getLeadComments(id: number) {
      const { response, error } = await getRequest<Comment[]>(
        `crm/leads/${id}/comments`,
      );
      if (!error) {
        this._leadComments = response.data;
      }
    },

    // mutations
    selectionChange(arr: Array<Lead>) {
      this.multipleSelection = arr;
      console.log(this.multipleSelection, "kkk");
      this.selectedLeads = arr;
    },
    toggleLeadsFiltersEditModal() {
      this.$state.showLeadsFiltersEditModal =
        !this.$state.showLeadsFiltersEditModal;
    },
    closeLeadEditModal() {
      this.$state.showLeadEditModal = false;
    },
    openLeadEditModal() {
      this.showLeadEditModal = true;
    },
    closeTableLeadEditModal() {
      this.showTableLeadEditModal = false;
    },
    openTableLeadEditModal() {
      this.showTableLeadEditModal = true;
    },
    setBulkUploadModalVisibility(value: boolean) {
      this.showBulkUpload = value;
    },
    setFormSubmissionUrlModalVisibility(value: boolean) {
      this._showFormSubmissionUrlModal = value;
    },
    setReassignModalVisibility(value: boolean) {
      this.showReassign = value;
    },
    setEditLeadData(lead: Lead) {
      this.$state._selectedLeadForEdit = lead;
      this.$state._lead = lead;
    },
    setDuplicateLeadData(lead: Lead) {
      this.$state._selectedLeadForDuplicate = lead;
    },
    setSubmittedFilterData(filter: any) {
      this.$state._submittedFilterData = filter;
    },
    setLeadCreateDialogVisibility(value: boolean) {
      this.$state.showListCreateDialog = value;
    },
    setLeadDuplicateDialogVisibility(value: boolean) {
      this.$state.showListDuplicateDialog = value;
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
    toggleLeadCreateModal() {
      this.showLeadCreateModal = !this.showLeadCreateModal;
    },
    closeLeadCreateModal() {
      this.showLeadCreateModal = false;
    },
    toggleCreateDialog() {
      this.showCreateDialog = !this.showCreateDialog;
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
    },
    setLeadableData(
      leadableData: Pick<Lead, "leadable_id" | "leadable_type"> & {
        channel_id?: number;
        channel_type?: "meta_page" | "whatsapp_account";
        referral_id?: number | null;
        referral?: Referral | null;
      },
    ) {
      this.$state._leadableData = leadableData;
    },
    clearLeadableData() {
      this.$state._leadableData = null;
    },
    setView(view: LeadView) {
      this.$state._view = view;
    },
    relocateLead(
      newStatusGroupName: string,
      leadId: number,
      updatedStatusId: number,
      updatedStatusObject: LeadStatus,
      newPosition: number,
    ) {
      let leadToMove = {} as Lead;
      let previousGroup:
        | { name: string; leads: Lead[]; count: number }
        | undefined;

      this._statusLeads.find((group) => {
        const leadIndex = group.leads.findIndex((lead) => lead.id === leadId);
        if (leadIndex !== -1) {
          leadToMove = group.leads.splice(leadIndex, 1)[0];
          previousGroup = group;
        }
      });

      if (!leadToMove) {
        console.log(`Lead with ID ${leadId} not found.`);
        return;
      }

      if (previousGroup) {
        previousGroup.count = Math.max(0, (previousGroup.count || 0) - 1);
      }

      leadToMove.status_id = updatedStatusId;
      leadToMove.status = updatedStatusObject;
      leadToMove.position = newPosition;

      let targetGroup = this._statusLeads.find(
        (group) => group.name === newStatusGroupName,
      );

      if (!targetGroup) {
        targetGroup = { name: newStatusGroupName, leads: [], count: 0 };
        this._statusLeads.push(targetGroup);
      }

      targetGroup.count = (targetGroup.count || 0) + 1;

      const insertIndex = targetGroup.leads.findIndex(
        (lead) => lead.position > newPosition,
      );

      if (insertIndex === -1) {
        targetGroup.leads.push(leadToMove);
      } else {
        targetGroup.leads.splice(insertIndex, 0, leadToMove);
      }
    },
  },
});
